import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import LanguageSwitcher from "./LanguageSwitcher";

interface Props {
  step: number;
  total: number;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  onSkip?: () => void;
  children: React.ReactNode;
}

/**
 * One focal decision per screen. Calm progress at the top, content centered and
 * narrow (readable + thumb-friendly), a primary CTA anchored to the bottom that
 * becomes a sticky bar on small screens. Mobile-first.
 */
export default function StepLayout({
  step, total, title, subtitle, onBack, onNext, nextLabel = "Verder",
  nextDisabled, onSkip, children,
}: Props) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      {/* Progress + back */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          aria-label={t("common.back")}
          onClick={onBack}
          disabled={!onBack}
          sx={{ visibility: onBack ? "visible" : "hidden", color: "text.secondary" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <LinearProgress
          variant="determinate"
          value={(step / total) * 100}
          sx={{
            flex: 1, height: 6, borderRadius: 999, bgcolor: "var(--primary-100)",
            "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" },
          }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 34, textAlign: "right" }}>
          {step}/{total}
        </Typography>
        <LanguageSwitcher dense />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1, width: "100%", maxWidth: 600, mx: "auto",
          px: { xs: 2.5, sm: 3 }, pt: { xs: 5, sm: 8 }, pb: 16,
          display: "flex", flexDirection: "column",
        }}
      >
        <Typography variant="h2" sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem" }, mb: 1 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "text.secondary", mb: 4, fontSize: "1.05rem" }}>{subtitle}</Typography>
        )}
        <Box sx={{ mt: subtitle ? 0 : 3 }}>{children}</Box>
      </Box>

      {/* Fixed footer CTA — always visible on mobile (sticky is unreliable with
          mobile dynamic toolbars / safe-area). Content above has bottom padding
          to clear it. */}
      {onNext && (
        <Box
          sx={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10,
            bgcolor: "rgba(250,243,233,0.92)",
            backdropFilter: "blur(8px)", borderTop: "1px solid var(--border)",
            px: { xs: 2.5, sm: 3 }, pt: 2,
            pb: "calc(16px + env(safe-area-inset-bottom))",
          }}
        >
          <Box sx={{ maxWidth: 600, mx: "auto", display: "flex", alignItems: "center", gap: 2 }}>
            {onSkip && (
              <Button onClick={onSkip} sx={{ color: "text.secondary" }}>
                {t("common.skip")}
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={onNext}
              disabled={nextDisabled}
              sx={{ ml: "auto", minWidth: { xs: 0, sm: 200 }, flex: { xs: 1, sm: "initial" } }}
            >
              {nextLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
